import { useState, useEffect } from 'react'
import {
  MdRocketLaunch,
  MdFlight,
  MdGroup,
  MdSpeed,
  MdBusiness,
  MdLocationOn,
  MdTrendingUp,
  MdRefresh,
} from 'react-icons/md'
import { WelcomeCard, StatsCard, AirlineCard, AnalyticsCard } from '../../components/cards'
import { Button, LoadingSpinner, EmptyState, SearchBar, FilterBar } from '../../components/ui'
import { useAirline } from '../../hooks/useAirline'
import { useWebsocket } from '../../hooks/useWebsocket'
import { getWebsocketChannel } from '../../utils/websocket'

function DashboardPage() {
  const [airlines, setAirlines] = useState([])
  const [filteredAirlines, setFilteredAirlines] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  const { getAirlinesAsync, isGetAirlinesLoading } = useAirline()

  useEffect(() => {
    const channelName = getWebsocketChannel().dashboard

    if (channelName) {
      subscribeToChannel(channelName)
    }

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch airlines data
  useEffect(() => {
    const fetchAirlines = async () => {
      try {
        setLoading(true)
        const data = await getAirlinesAsync()
        if (data) {
          setAirlines(data)
          setFilteredAirlines(data)
        }
      } catch (err) {
        console.error('Error fetching airlines:', err)
        setError('Failed to load airline data')
      } finally {
        setLoading(false)
      }
    }

    fetchAirlines()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Filter airlines based on search and status
  useEffect(() => {
    let filtered = airlines

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (airline) =>
          airline.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          airline.iataCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          airline.country?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(
        (airline) => airline.status?.toLowerCase() === statusFilter.toLowerCase(),
      )
    }

    setFilteredAirlines(filtered)
  }, [airlines, searchTerm, statusFilter])

  // Calculate dashboard statistics
  const dashboardStats = {
    totalAirlines: airlines.length,
    activeAirlines: airlines.filter((a) => a.status?.toLowerCase() === 'active').length,
    totalFleetSize: airlines.reduce((sum, airline) => sum + (airline.fleetSize || 0), 0),
    totalDestinations: airlines.reduce(
      (sum, airline) => sum + (airline.destinations?.length || 0),
      0,
    ),
    totalRevenue: airlines.reduce(
      (sum, airline) => sum + (airline.financialInfo?.annualRevenue || 0),
      0,
    ),
    averageFleetSize:
      airlines.length > 0
        ? Math.round(
            airlines.reduce((sum, airline) => sum + (airline.fleetSize || 0), 0) / airlines.length,
          )
        : 0,
  }

  // Get top airlines by revenue
  const topAirlinesByRevenue = [...airlines]
    .sort((a, b) => (b.financialInfo?.annualRevenue || 0) - (a.financialInfo?.annualRevenue || 0))
    .slice(0, 5)

  // Get airlines by status
  const airlinesByStatus = {
    active: airlines.filter((a) => a.status?.toLowerCase() === 'active').length,
    inactive: airlines.filter((a) => a.status?.toLowerCase() === 'inactive').length,
    suspended: airlines.filter((a) => a.status?.toLowerCase() === 'suspended').length,
  }

  const handleAirlineClick = (airline) => {
    // TODO: Navigate to airline detail page
    console.log('Airline clicked:', airline)
  }

  const handleRefresh = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getAirlinesAsync()
      if (data) {
        setAirlines(data)
        setFilteredAirlines(data)
      }
    } catch (err) {
      console.error('Error refreshing airlines:', err)
      setError('Failed to refresh airline data')
    } finally {
      setLoading(false)
    }
  }

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
  ]

  if (loading && airlines.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error && airlines.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <EmptyState
          icon={<MdFlight className="text-4xl text-neutral-400" />}
          title="Failed to load airlines"
          description={error}
          action={
            <Button onClick={handleRefresh} variant="primary">
              Try Again
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <WelcomeCard
          icon={<MdRocketLaunch className="text-white text-xl" />}
          title="Airline Management Dashboard"
          description="Monitor and manage your airline operations with real-time analytics and insights"
          className="col-span-full"
        />
      </div>

      {/* Analytics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <AnalyticsCard
          title="Total Airlines"
          value={dashboardStats.totalAirlines}
          icon={<MdFlight />}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        />

        <AnalyticsCard
          title="Active Airlines"
          value={dashboardStats.activeAirlines}
          change={
            dashboardStats.totalAirlines > 0
              ? Math.round((dashboardStats.activeAirlines / dashboardStats.totalAirlines) * 100)
              : 0
          }
          changeType="positive"
          icon={<MdBusiness />}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />

        <AnalyticsCard
          title="Total Fleet Size"
          value={dashboardStats.totalFleetSize}
          icon={<MdGroup />}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
          subtitle={`Avg: ${dashboardStats.averageFleetSize} per airline`}
        />

        <AnalyticsCard
          title="Total Destinations"
          value={dashboardStats.totalDestinations}
          icon={<MdLocationOn />}
          iconBgColor="bg-purple-100 dark:bg-purple-900"
          iconColor="text-purple-600 dark:text-purple-400"
        />
      </div>

      {/* Status Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatsCard
          label="Active Airlines"
          value={airlinesByStatus.active}
          icon={<MdTrendingUp />}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />

        <StatsCard
          label="Inactive Airlines"
          value={airlinesByStatus.inactive}
          icon={<MdSpeed />}
          iconBgColor="bg-neutral-100 dark:bg-neutral-800"
          iconColor="text-neutral-600 dark:text-neutral-400"
        />

        <StatsCard
          label="Suspended Airlines"
          value={airlinesByStatus.suspended}
          icon={<MdSpeed />}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        />
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <SearchBar
            placeholder="Search airlines by name, IATA code, or country..."
            searchTerm={searchTerm}
            onSearchChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-none"
          />
        </div>
        <div className="flex gap-2">
          <FilterBar
            filterOptions={statusOptions}
            filterValue={statusFilter}
            onFilterChange={(e) => setStatusFilter(e.target.value)}
          />
          <Button
            onClick={handleRefresh}
            variant="outline"
            loading={isGetAirlinesLoading}
            className="whitespace-nowrap"
          >
            <MdRefresh className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Airlines Grid */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
            Airlines ({filteredAirlines.length})
          </h2>
          {filteredAirlines.length !== airlines.length && (
            <Button
              onClick={() => {
                setSearchTerm('')
                setStatusFilter('all')
              }}
              variant="ghost"
              size="sm"
            >
              Clear Filters
            </Button>
          )}
        </div>

        {filteredAirlines.length === 0 ? (
          <EmptyState
            icon={<MdFlight className="text-4xl text-neutral-400" />}
            title="No airlines found"
            description={
              searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'No airlines available in the system'
            }
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredAirlines.map((airline, index) => (
              <AirlineCard
                key={airline.id || index}
                airline={airline}
                onClick={() => handleAirlineClick(airline)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Top Airlines by Revenue */}
      {topAirlinesByRevenue.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">
            Top Airlines by Revenue
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {topAirlinesByRevenue.map((airline, index) => (
              <AirlineCard
                key={airline.id || index}
                airline={airline}
                onClick={() => handleAirlineClick(airline)}
                className="border-2 border-primary-200 dark:border-primary-800"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardPage
